import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';

function UserSettings({ token, backendUrl }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: '',
    password: '********',
    full_name: '',
    phone_number: '',
    address: '',
    email: '',
    telegram_chat_id: '',
    telegram_id: '',
    telegram_name: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // 改密碼
  const [showPwForm, setShowPwForm] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState(null);
  const [pwSuccess, setPwSuccess] = useState(null);

  const authHeaders = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  useEffect(() => {
    if (!token) return undefined;
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${backendUrl}/users/me`, { ...authHeaders, signal: controller.signal });
        if (controller.signal.aborted) return;
        const data = res.data || {};
        setFormData({
          username: data.username || '',
          password: data.password || '********',
          full_name: data.full_name || '',
          phone_number: data.phone_number || '',
          address: data.address || '',
          email: data.email || '',
          telegram_chat_id: data.telegram_chat_id || '',
          telegram_id: data.telegram_id || '',
          telegram_name: data.telegram_name || '',
        });
      } catch (err) {
        if (axios.isCancel(err) || err.name === 'CanceledError') return;
        setError(err.response?.data?.detail || err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [token, backendUrl, authHeaders]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      setError(t('settings.invalidEmail'));
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${backendUrl}/users/me`, formData, authHeaders);
      setSuccess(t('settings.updateSuccess'));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError(null);
    setPwSuccess(null);

    if (newPassword.length < 6) {
      setPwError(t('settings.pwMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError(t('settings.pwMismatch'));
      return;
    }

    setPwSaving(true);
    try {
      await axios.put(`${backendUrl}/users/me/password`, {
        old_password: oldPassword,
        new_password: newPassword,
      }, authHeaders);
      setPwSuccess(t('settings.pwChangeSuccess'));
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPwForm(false);
    } catch (err) {
      setPwError(err.response?.data?.detail || err.message);
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="container py-4">
      <h2 className="mb-4">{t('settings.title')}</h2>

      {error && (
        <div className="alert alert-danger" role="alert">{t('common.error')}: {error}</div>
      )}
      {success && (
        <div className="alert alert-success" role="alert">{success}</div>
      )}

      {loading ? (
        <div className="text-center my-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      ) : (
        <>
          <form onSubmit={handleSubmit} className="card shadow-sm p-4 mb-4">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label" htmlFor="username">{t('settings.username')}</label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  className="form-control"
                  value={formData.username}
                  onChange={handleChange}
                  disabled
                />
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="password">{t('settings.password')}</label>
                <div className="input-group">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    className="form-control"
                    value={formData.password}
                    disabled
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => { setShowPwForm(!showPwForm); setPwError(null); setPwSuccess(null); }}
                  >
                    <i className="bi bi-key me-1"></i>{t('settings.changePassword')}
                  </button>
                </div>
              </div>

              <div className="col-md-6">
                <label className="form-label" htmlFor="full_name">{t('settings.fullName')}</label>
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  className="form-control"
                  value={formData.full_name}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="email">{t('settings.email')}</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@example.com"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label" htmlFor="phone_number">{t('settings.phone')}</label>
                <input
                  id="phone_number"
                  name="phone_number"
                  type="text"
                  className="form-control"
                  value={formData.phone_number}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="telegram_chat_id">{t('settings.telegramChatId')}</label>
                <input
                  id="telegram_chat_id"
                  name="telegram_chat_id"
                  type="text"
                  className="form-control"
                  value={formData.telegram_chat_id}
                  placeholder={t('settings.telegramChatIdPlaceholder')}
                  readOnly
                  disabled
                />
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="telegram_id">{t('settings.telegramId')}</label>
                <input
                  id="telegram_id"
                  name="telegram_id"
                  type="text"
                  className="form-control"
                  value={formData.telegram_id}
                  onChange={handleChange}
                  placeholder={t('settings.telegramIdPlaceholder')}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="telegram_name">{t('settings.telegramName')}</label>
                <input
                  id="telegram_name"
                  name="telegram_name"
                  type="text"
                  className="form-control"
                  value={formData.telegram_name}
                  onChange={handleChange}
                  placeholder={t('settings.telegramNamePlaceholder')}
                />
              </div>

              <div className="col-12">
                <label className="form-label" htmlFor="address">{t('settings.address')}</label>
                <textarea
                  id="address"
                  name="address"
                  className="form-control"
                  rows="2"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="mt-4 d-flex gap-2">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {t('common.saving')}
                  </>
                ) : t('common.save')}
              </button>
            </div>
          </form>

          {/* 改密碼表單 */}
          {showPwForm && (
            <div className="card shadow-sm p-4">
              <h5 className="mb-3"><i className="bi bi-key me-2"></i>{t('settings.changePassword')}</h5>

              {pwError && (
                <div className="alert alert-danger py-2">{t('common.error')}: {pwError}</div>
              )}
              {pwSuccess && (
                <div className="alert alert-success py-2">{pwSuccess}</div>
              )}

              <form onSubmit={handleChangePassword}>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">{t('settings.oldPassword')}</label>
                    <input
                      type="password"
                      className="form-control"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">{t('settings.newPassword')}</label>
                    <input
                      type="password"
                      className="form-control"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">{t('settings.confirmPassword')}</label>
                    <input
                      type="password"
                      className="form-control"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                  </div>
                </div>
                <div className="mt-3 d-flex gap-2">
                  <button type="submit" className="btn btn-warning" disabled={pwSaving}>
                    {pwSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('common.saving')}
                      </>
                    ) : t('settings.changePassword')}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPwForm(false)}>
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default UserSettings;
